import { reportCacheFull, reportCacheWritable } from "./cacheHealth";
import { writeWithQuotaGuard } from "./storageQuota";
import { liveQuery } from "dexie";

import type {
  LocalShowTrackingSnapshot,
  LocalTrackingEpisode,
  ShowTrackingSnapshot,
  ShowTrackingSummary,
  TrackingEpisodePatch,
  TrackingMutationResultV2,
  UpNextEntry,
  WatchHistoryEntry,
} from "../features/tracking/types";
import {
  type ClientNoticeRecord,
  type OfflineDatabase,
  offlineDatabase,
  offlineWritesRetiredNoticeId,
  type TrackingEpisodeRecord,
} from "./database";

const UP_NEXT_KEY = "tracking:up-next";

/**
 * The v1 snapshot as a summary, for the cache.
 *
 * `applyOperation` still returns the full snapshot shape, which carries no
 * per-installment counts and no ordering inventory -- the summary query is
 * where those come from. Both are derived rather than invented: the season
 * checkboxes read the installment counts, and a fabricated one would draw a
 * season as finished that is not.
 */
function summaryFromSnapshot(snapshot: ShowTrackingSnapshot): ShowTrackingSummary {
  const { episodes, ...summary } = snapshot;
  const counts = new Map<string, { watchedCount: number; episodeCount: number }>();
  for (const episode of episodes) {
    if (episode.installmentPublicId === null) continue;
    const count = counts.get(episode.installmentPublicId) ?? { watchedCount: 0, episodeCount: 0 };
    count.episodeCount += 1;
    if (episode.watchCount > 0) count.watchedCount += 1;
    counts.set(episode.installmentPublicId, count);
  }
  return {
    ...summary,
    projectionStatus: "ready",
    availableOrderings: [snapshot.orderingKind],
    installments: [...counts].map(([installmentPublicId, count]) => ({
      installmentPublicId,
      ...count,
    })),
  };
}

function trackingEpisodeRecord(
  ownerId: string,
  showPublicId: string,
  episode: ShowTrackingSnapshot["episodes"][number],
  updatedAt: number,
): TrackingEpisodeRecord {
  return {
    ownerId,
    episodePublicId: episode.episodePublicId,
    showPublicId,
    installmentPublicId: episode.installmentPublicId,
    payload: episode,
    updatedAt,
  };
}

export async function persistAuthoritativeShowSnapshotUnguarded(
  database: OfflineDatabase,
  ownerId: string,
  authoritative: ShowTrackingSnapshot,
) {
  await database.transaction("rw", database.trackingShows, database.trackingEpisodes, async () => {
    await database.trackingShows.put({
      ownerId,
      showPublicId: authoritative.showPublicId,
      payload: summaryFromSnapshot(authoritative),
      updatedAt: authoritative.updatedAt,
    });
    /*
     * Membership is replaced, not merged.
     *
     * `bulkPut` updates the rows still present and leaves everything else
     * behind, so an episode the provider deprecated, removed or moved to
     * another installment stayed in this browser's cache for good --
     * `readTrackingShowSnapshot` reads every row for the owner and show, so it
     * kept appearing offline long after it had gone. The snapshot is the whole
     * truth for this show; anything it omits is no longer part of it.
     */
    const keep = new Set(authoritative.episodes.map((episode) => episode.episodePublicId));
    // The primary key is `[ownerId, episodePublicId]`, so the second element
    // says which episode a stale row belongs to.
    const cached = await database.trackingEpisodes
      .where("[ownerId+showPublicId]")
      .equals([ownerId, authoritative.showPublicId])
      .primaryKeys();
    await database.trackingEpisodes.bulkDelete(
      cached.filter(([, episodePublicId]) => !keep.has(episodePublicId)),
    );
    await database.trackingEpisodes.bulkPut(
      authoritative.episodes.map((episode) =>
        trackingEpisodeRecord(
          ownerId,
          authoritative.showPublicId,
          episode,
          authoritative.updatedAt,
        ),
      ),
    );
  });
}

export async function persistAuthoritativeShowSummaryUnguarded(
  database: OfflineDatabase,
  ownerId: string,
  summary: ShowTrackingSummary,
) {
  await database.trackingShows.put({
    ownerId,
    showPublicId: summary.showPublicId,
    payload: summary,
    updatedAt: summary.updatedAt,
  });
}

async function persistEpisodePatch(
  database: OfflineDatabase,
  ownerId: string,
  showPublicId: string,
  patch: TrackingEpisodePatch,
  updatedAt: number,
) {
  const existing = await database.trackingEpisodes.get([ownerId, patch.episodePublicId]);
  const existingPayload =
    typeof existing?.payload === "object" && existing.payload !== null ? existing.payload : {};
  await database.trackingEpisodes.put({
    ownerId,
    episodePublicId: patch.episodePublicId,
    showPublicId,
    installmentPublicId: existing?.installmentPublicId ?? null,
    payload: { ...existingPayload, ...patch },
    updatedAt,
  });
}

async function persistUpNextForShow(
  database: OfflineDatabase,
  ownerId: string,
  showPublicId: string,
  entry: UpNextEntry | null,
) {
  const existing = await readUpNextSnapshot(ownerId, database);
  const withoutShow = existing.filter((item) => item.showPublicId !== showPublicId);
  await persistUpNextSnapshot(
    database,
    ownerId,
    entry === null ? withoutShow : [entry, ...withoutShow],
  );
}

export async function persistTrackingMutationResult(
  database: OfflineDatabase,
  ownerId: string,
  result: TrackingMutationResultV2,
) {
  await database.transaction("rw", database.trackingShows, database.trackingEpisodes, async () => {
    await persistAuthoritativeShowSummary(database, ownerId, result.show);
    await Promise.all(
      result.changedEpisodes.map(async (patch) => {
        await persistEpisodePatch(
          database,
          ownerId,
          result.show.showPublicId,
          patch,
          result.show.updatedAt,
        );
      }),
    );
  });
  await persistUpNextForShow(database, ownerId, result.show.showPublicId, result.upNext);
}

export async function persistUpNextSnapshotUnguarded(
  database: OfflineDatabase,
  ownerId: string,
  entries: UpNextEntry[],
) {
  await database.privateSnapshots.put({
    ownerId,
    key: UP_NEXT_KEY,
    payload: entries,
    updatedAt: Date.now(),
  });
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

async function localEpisodeFromRecord(
  database: OfflineDatabase,
  record: TrackingEpisodeRecord,
): Promise<LocalTrackingEpisode> {
  const payload = record.payload as Partial<LocalTrackingEpisode>;
  const catalogRecord = await database.catalogEpisodes.get(record.episodePublicId);
  const catalog = (catalogRecord?.payload ?? {}) as {
    title?: unknown;
    displayNumber?: unknown;
    sortKey?: unknown;
    releaseAt?: unknown;
    releaseDate?: unknown;
  };
  return {
    episodePublicId: record.episodePublicId,
    installmentPublicId: record.installmentPublicId,
    installmentType: payload.installmentType ?? null,
    title: optionalString(payload.title ?? catalog.title),
    displayNumber: optionalString(payload.displayNumber ?? catalog.displayNumber),
    sortKey: optionalNumber(payload.sortKey ?? catalog.sortKey),
    releaseAt: optionalNumber(payload.releaseAt ?? catalog.releaseAt),
    releaseDate: optionalString(payload.releaseDate ?? catalog.releaseDate),
    watchCount: typeof payload.watchCount === "number" ? payload.watchCount : 0,
    lastWatchedAt: optionalNumber(payload.lastWatchedAt),
    lastWatchOperationId: optionalString(payload.lastWatchOperationId),
    orderings: Array.isArray(payload.orderings) ? payload.orderings : [],
  };
}

export async function readTrackingShowSnapshot(
  ownerId: string,
  showPublicId: string,
  database = offlineDatabase,
): Promise<LocalShowTrackingSnapshot | null> {
  const summaryRecord = await database.trackingShows.get([ownerId, showPublicId]);
  if (summaryRecord === undefined) return null;
  /*
   * Typed as what an older client may have written, not as what the current
   * server returns. A row cached before following replaced the library state
   * carries none of the fields below, and asserting the current shape over it
   * would turn a missing value into a confident wrong one.
   */
  const summary = summaryRecord.payload as Omit<
    ShowTrackingSummary,
    "followed" | "notifyOnRelease" | "availableOrderings" | "installments"
  > &
    Partial<
      Pick<
        ShowTrackingSummary,
        "followed" | "notifyOnRelease" | "availableOrderings" | "installments"
      >
    >;
  const progressRecords = await database.trackingEpisodes
    .where("[ownerId+showPublicId]")
    .equals([ownerId, showPublicId])
    .toArray();
  const episodes = await Promise.all(
    progressRecords.map(async (record) => await localEpisodeFromRecord(database, record)),
  );
  episodes.sort(
    (left, right) =>
      (left.sortKey ?? Number.MAX_SAFE_INTEGER) - (right.sortKey ?? Number.MAX_SAFE_INTEGER),
  );
  return {
    showPublicId: summary.showPublicId,
    showTitles: summary.showTitles,
    /*
     * Defaulted, because a snapshot written before following replaced the
     * library state carries neither field. Reading it as "not followed, not
     * announced" is the honest answer offline: the row that decides both lives
     * on the server, and this cache is read-only until it reconnects.
     */
    followed: summary.followed ?? false,
    notifyOnRelease: summary.notifyOnRelease ?? false,
    orderingKind: summary.orderingKind,
    serverVersion: summary.serverVersion,
    watchedEpisodeCount: summary.watchedEpisodeCount,
    totalEligibleEpisodes: summary.totalEligibleEpisodes,
    completionPercentage: summary.completionPercentage,
    updatedAt: summary.updatedAt,
    availableOrderings: summary.availableOrderings ?? ["aired"],
    installments: summary.installments ?? [],
    episodes,
    history: summary.history,
    lastUndoableBulk: summary.lastUndoableBulk,
  };
}

export async function readUpNextSnapshot(ownerId: string, database = offlineDatabase) {
  const record = await database.privateSnapshots.get([ownerId, UP_NEXT_KEY]);
  return (record?.payload as UpNextEntry[] | undefined) ?? [];
}

export function observeTrackingShowSnapshot(ownerId: string, showPublicId: string) {
  return liveQuery(async () => await readTrackingShowSnapshot(ownerId, showPublicId));
}

export function observeUpNextSnapshot(ownerId: string) {
  return liveQuery(async () => await readUpNextSnapshot(ownerId));
}

export async function rememberCurrentOwner(
  ownerId: string,
  database: OfflineDatabase = offlineDatabase,
) {
  await database.localSession.put({ key: "current-owner", ownerId, updatedAt: Date.now() });
}

export async function readCurrentOwner(database: OfflineDatabase = offlineDatabase) {
  return (await database.localSession.get("current-owner"))?.ownerId ?? null;
}

export async function readOfflineWritesRetiredNotice(
  ownerId: string,
  database: OfflineDatabase = offlineDatabase,
): Promise<ClientNoticeRecord | null> {
  return (await database.clientNotices.get(offlineWritesRetiredNoticeId(ownerId))) ?? null;
}

export function observeOfflineWritesRetiredNotice(ownerId: string) {
  return liveQuery(async () => await readOfflineWritesRetiredNotice(ownerId));
}

export async function dismissOfflineWritesRetiredNotice(
  ownerId: string,
  database: OfflineDatabase = offlineDatabase,
) {
  await database.clientNotices.delete(offlineWritesRetiredNoticeId(ownerId));
}

export type { WatchHistoryEntry };

/**
 * Guarded so a full origin degrades the cache instead of rejecting into
 * whatever called it -- most of these are invoked as `void`, where a rejection
 * is an unhandled promise and nothing else.
 */
export async function persistAuthoritativeShowSnapshot(
  ...args: Parameters<typeof persistAuthoritativeShowSnapshotUnguarded>
) {
  const written = await writeWithQuotaGuard(() =>
    persistAuthoritativeShowSnapshotUnguarded(...args),
  );
  if (written) reportCacheWritable();
  else reportCacheFull();
  return written;
}

/**
 * Guarded so a full origin degrades the cache instead of rejecting into
 * whatever called it -- most of these are invoked as `void`, where a rejection
 * is an unhandled promise and nothing else.
 */
export async function persistAuthoritativeShowSummary(
  ...args: Parameters<typeof persistAuthoritativeShowSummaryUnguarded>
) {
  const written = await writeWithQuotaGuard(() =>
    persistAuthoritativeShowSummaryUnguarded(...args),
  );
  if (written) reportCacheWritable();
  else reportCacheFull();
  return written;
}

/**
 * Guarded so a full origin degrades the cache instead of rejecting into
 * whatever called it -- most of these are invoked as `void`, where a rejection
 * is an unhandled promise and nothing else.
 */
export async function persistUpNextSnapshot(
  ...args: Parameters<typeof persistUpNextSnapshotUnguarded>
) {
  const written = await writeWithQuotaGuard(() => persistUpNextSnapshotUnguarded(...args));
  if (written) reportCacheWritable();
  else reportCacheFull();
  return written;
}
